<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import type { UrlFilterValues } from "@/composables/useUrlFilters";
import { SPECIES_SORT_VALUES, useSpeciesSortOptions, type SpeciesSort } from "@/composables/useSpeciesSortOptions";

definePageMeta({
  name: "observations",
  layout: "app",
});

const { t } = useI18n();
const localePath = useLocalePath();

const viewMode = useUrlViewMode();

const page = useUrlPage();
const listPage = computed(() => (viewMode.value === "list" ? page.value : 1));

const { filter: locationFilter, clear: clearLocation, chip: locationChip } = useUrlLocationFilter({ withRadius: true });

const sortOptions = useSpeciesSortOptions();
const sort = useUrlSort<SpeciesSort>("sort", "observations_desc", SPECIES_SORT_VALUES);

const filterCount = useUrlFilterCount(() => ["mine", "withPhoto", "taxons", "specieFeatures", "q"]);

const { title, breadcrumbLabel } = usePageTitle({
  base: () => t("observations.title"),
  page: listPage,
  filterCount,
});

useSeoMeta({
  title: () => title.value,
  description: () => t("seo.observations.description"),
});

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: breadcrumbLabel.value },
]);

const filterValues = ref<UrlFilterValues>({});
const total = ref<number>();
const loading = ref(false);
</script>

<template>
  <div class="relative flex flex-col grow">
    <AppSection :ui="{ base: 'pt-8 pb-4' }">
      <WebBreadcrumbs :items="breadcrumb" class="mb-10" />

      <WebLocationSearch v-model:filter="locationFilter" with-radius :placeholder="t('observations.search')" class="mb-4" />

      <WebFiltersObservations v-model:applied="filterValues" :search-chip="locationChip" @clear:search="clearLocation" />
    </AppSection>

    <AppSection class="grow" :ui="{ base: 'pb-4 pt-6' }">
      <WebTabsRow>
        <template #left>
          <AppResultCount :loading="loading">
            <template v-if="total !== undefined">{{ t("observations.speciesCount", total) }}</template>
          </AppResultCount>
        </template>
        <template #center>
          <WebListMapTabs
            v-model="viewMode"
            tab-id-list="observations-tab-list"
            tab-id-map="observations-tab-map"
            panel-id-list="observations-panel-list"
            panel-id-map="observations-panel-map"
          />
        </template>
        <template #right>
          <AppSortSelect v-if="viewMode === 'list'" v-model="sort" :options="sortOptions" />
        </template>
      </WebTabsRow>
    </AppSection>

    <div
      v-if="viewMode === 'list'"
      id="observations-panel-list"
      role="tabpanel"
      aria-labelledby="observations-tab-list"
      class="flex flex-col grow"
    >
      <WebObservationsListView v-model:sort="sort" v-model:total="total" v-model:loading="loading" :filter="locationFilter" />
    </div>
    <AppSection
      v-else
      id="observations-panel-map"
      role="tabpanel"
      aria-labelledby="observations-tab-map"
      class="grow flex flex-col lg:pb-24"
    >
      <WebObservationsMapView
        class="h-[calc(var(--ui-content-height)+16px)] lg:h-[calc(var(--ui-content-height)-(--spacing(8)))] min-h-82 max-sm:-mx-4 sm:max-lg:-mx-6 -mb-4"
        v-model:total="total"
        v-model:loading="loading"
        v-model:view-mode="viewMode"
        :filter="locationFilter"
      />
    </AppSection>

    <WebListMapMobileButton v-if="viewMode === 'list'" v-model="viewMode" />
  </div>
</template>
